import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import {
  HiOutlineMagnifyingGlass,
  HiOutlineUser,
  HiOutlineBuildingOffice2,
  HiOutlineExclamationTriangle,
  HiOutlineBriefcase,
  HiOutlineCalendarDays,
  HiOutlineDocumentText,
  HiOutlineAcademicCap,
  HiOutlineUserGroup,
  HiOutlineCheckCircle,
} from 'react-icons/hi2'
import { useUIStore } from '@/store/ui'
import { searchApi, type SearchResult } from '@/services/api/search'
import { useDebounce } from '@/hooks/useDebounce'
import type { EntityType } from '@/types'
import { cn } from '@/utils/cn'

const typeIcon: Record<EntityType, JSX.Element> = {
  stakeholder: <HiOutlineUser />,
  business: <HiOutlineBuildingOffice2 />,
  skill: <HiOutlineAcademicCap />,
  organization: <HiOutlineUserGroup />,
  opportunity: <HiOutlineBriefcase />,
  project: <HiOutlineBriefcase />,
  grievance: <HiOutlineExclamationTriangle />,
  meeting: <HiOutlineCalendarDays />,
  task: <HiOutlineCheckCircle />,
  document: <HiOutlineDocumentText />,
  user: <HiOutlineUser />,
}

const QUICK_LINKS: { label: string; to: string }[] = [
  { label: 'Go to Dashboard', to: '/dashboard' },
  { label: 'View Contacts', to: '/contacts' },
  { label: 'Open Grievances', to: '/issues' },
  { label: 'Today\'s Calendar', to: '/calendar' },
  { label: 'Constituency Map', to: '/maps' },
  { label: 'Reports & Analytics', to: '/reports' },
]

export function CommandPalette() {
  const open = useUIStore((s) => s.commandOpen)
  const setOpen = useUIStore((s) => s.setCommandOpen)
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const navigate = useNavigate()
  const debounced = useDebounce(q, 200)

  const { data: results = [] } = useQuery({
    queryKey: ['command', debounced],
    queryFn: () => searchApi.global(debounced),
    enabled: debounced.length > 1,
  })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(!open)
      }
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, setOpen])

  useEffect(() => {
    if (!open) {
      setQ('')
      setActive(0)
    }
  }, [open])

  const go = (to: string) => {
    navigate(to)
    setOpen(false)
  }

  const list: (SearchResult | { label: string; to: string })[] =
    debounced.length > 1 ? results : QUICK_LINKS

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => Math.min(a + 1, list.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(a - 1, 0))
    } else if (e.key === 'Enter' && list[active]) {
      go((list[active] as any).to)
    }
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[1300] flex items-start justify-center p-4 pt-[12vh]">
          <motion.div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: -10 }}
            className="relative z-10 w-full max-w-xl overflow-hidden rounded-2xl bg-card shadow-elevated"
          >
            <div className="flex items-center gap-3 border-b border-border px-4">
              <HiOutlineMagnifyingGlass className="h-5 w-5 text-muted-foreground" />
              <input
                autoFocus
                value={q}
                onChange={(e) => {
                  setQ(e.target.value)
                  setActive(0)
                }}
                onKeyDown={onKeyDown}
                placeholder="Search contacts, grievances, opportunities…"
                className="h-14 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
              <span className="kbd">ESC</span>
            </div>
            <div className="max-h-[52dvh] overflow-y-auto p-2">
              {debounced.length > 1 && results.length === 0 ? (
                <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                  No results for “{debounced}”
                </p>
              ) : (
                <>
                  {debounced.length <= 1 && (
                    <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Quick navigation
                    </p>
                  )}
                  {list.map((item, i) => {
                    const isResult = 'type' in item
                    return (
                      <button
                        key={i}
                        onMouseEnter={() => setActive(i)}
                        onClick={() => go((item as any).to)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors',
                          active === i ? 'bg-primary-50 text-primary-900' : 'hover:bg-muted',
                        )}
                      >
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-base text-primary">
                          {isResult ? typeIcon[(item as SearchResult).type] : <HiOutlineMagnifyingGlass />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">
                            {isResult ? (item as SearchResult).title : (item as any).label}
                          </span>
                          {isResult && (
                            <span className="block truncate text-xs text-muted-foreground">
                              {(item as SearchResult).subtitle}
                            </span>
                          )}
                        </span>
                        {isResult && (
                          <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold capitalize text-muted-foreground">
                            {(item as SearchResult).type === 'stakeholder' ? 'contact' : (item as SearchResult).type}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </>
              )}
            </div>
            <div className="flex items-center justify-between border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="kbd">↑</span>
                <span className="kbd">↓</span> to navigate
              </span>
              <span className="flex items-center gap-1.5">
                <span className="kbd">↵</span> to open
              </span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
